import { useEffect, useState } from 'react';
import { useAuth } from '../../../context/AuthContext/AuthContext';
import GetDashboardSummaryService from '../services/get/summary/getSummary';

export type AffiliationType = 'AFFILIATION' | 'REFERRAL';

/**
 * Affiliation type of the signed-in client, read once per session from the partner dashboard
 * summary and shared by every caller (sidebar, router). `undefined` while unknown.
 *
 * Only clients with an affiliation deal (AFFILIATION) get the partner area; every other client
 * carries the default REFERRAL row and must not see it.
 */
let cachedType: AffiliationType | undefined;
let cachedToken: string | undefined;
let pending: Promise<AffiliationType | undefined> | undefined;
const listeners = new Set<(type: AffiliationType | undefined) => void>();

const load = (token: string): Promise<AffiliationType | undefined> => {
  if (cachedToken === token && cachedType !== undefined) return Promise.resolve(cachedType);
  if (cachedToken === token && pending) return pending;
  cachedToken = token;
  cachedType = undefined;
  pending = GetDashboardSummaryService.getDashboardSummary(token)
    .then((summary) => {
      const type = summary?.affiliationAttributes?.type === 'AFFILIATION' ? 'AFFILIATION' : 'REFERRAL';
      cachedType = type;
      listeners.forEach((listener) => listener(type));
      return type;
    })
    .catch(() => {
      // Unknown stays hidden: a failed call must not open the partner area.
      cachedType = 'REFERRAL';
      listeners.forEach((listener) => listener('REFERRAL'));
      return 'REFERRAL' as AffiliationType;
    })
    .finally(() => {
      pending = undefined;
    });
  return pending;
};

export interface AffiliationTypeState {
  affiliationType: AffiliationType | undefined;
  hasAffiliationDeal: boolean;
  loading: boolean;
}

export const useAffiliationType = (enabled = true): AffiliationTypeState => {
  const { tokens } = useAuth();
  const token = enabled ? tokens?.accessToken : undefined;
  const [type, setType] = useState<AffiliationType | undefined>(
    token && cachedToken === token ? cachedType : undefined,
  );

  useEffect(() => {
    if (!token) {
      setType(undefined);
      return undefined;
    }
    let active = true;
    const listener = (next: AffiliationType | undefined) => {
      if (active) setType(next);
    };
    listeners.add(listener);
    load(token).then(listener);
    return () => {
      active = false;
      listeners.delete(listener);
    };
  }, [token]);

  return {
    affiliationType: type,
    hasAffiliationDeal: type === 'AFFILIATION',
    loading: Boolean(token) && type === undefined,
  };
};
